import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, Monitor, Video, Instagram, Globe, Search, TrendingUp, MessageCircle, Youtube, Facebook, Sparkles, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import metaLogo from "@/assets/meta-logo.png";
import { randomHeroPair } from "@/components/site/Video";
import { PHONE_TEL, WHATSAPP_URL } from "@/lib/site";

const ROTATING_WORDS = [
  "Video",
  "Short-form video",
  "Video advertising",
  "A company film",
  "Social content",
];

const HeroV2 = () => {
  const [heroPair] = useState(randomHeroPair);
  const [clip, setClip] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const t = requestAnimationFrame(() => setIsLoaded(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((i) => (i + 1) % ROTATING_WORDS.length);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <header
      className="relative min-h-screen flex items-center pt-28 pb-20 md:pt-32 md:pb-24 lg:pt-36 lg:pb-28 overflow-hidden"
      role="banner"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0089B3] to-[#00a7de] transform translate-z-0" />
      <video className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity pointer-events-none" key={clip} src={heroPair[clip]} onEnded={() => setClip((c) => 1 - c)} autoPlay muted playsInline preload="metadata" aria-hidden="true" />
      <div className="absolute inset-0 opacity-20 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px]" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-[100px]" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <div className="space-y-6 text-center lg:text-left">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/25 text-white text-xs md:text-sm font-medium transition-all duration-700"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(8px)",
                }}
              >
                <span className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-current"
                      style={{
                        opacity: isLoaded ? 1 : 0,
                        transform: isLoaded ? "scale(1) rotate(0deg)" : "scale(0.4) rotate(-45deg)",
                        transition: "opacity 400ms ease-out, transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                        transitionDelay: `${300 + i * 110}ms`,
                      }}
                    />
                  ))}
                </span>
                Rated 5/5 on Google
              </div>

              <h1
                className="text-4xl xl:text-6xl font-bold text-white leading-[1.1] tracking-tight pb-2"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(16px)",
                  transition: "opacity 700ms ease-out, transform 700ms ease-out",
                  transitionDelay: "80ms",
                }}
              >
                <span
                  className="relative block h-[1.15em] overflow-hidden whitespace-nowrap text-center lg:inline-block lg:text-left lg:align-bottom w-full max-w-full lg:w-[16ch]"
                  aria-live="polite"
                >
                  {ROTATING_WORDS.map((word, i) => (
                    <span
                      key={word}
                      className="absolute left-0 right-0 top-0 transition-all duration-500 ease-out whitespace-nowrap text-center lg:text-left"
                      style={{
                        opacity: i === wordIndex ? 1 : 0,
                        transform:
                          i === wordIndex
                            ? "translateY(0)"
                            : i === (wordIndex - 1 + ROTATING_WORDS.length) % ROTATING_WORDS.length
                            ? "translateY(-100%)"
                            : "translateY(100%)",
                      }}
                      aria-hidden={i !== wordIndex}
                    >
                      <span className="relative inline-block">
                        {word}
                        <svg
                          className="absolute left-0 right-0"
                          style={{ top: "100%", width: "100%", height: 6, marginTop: 2 }}
                          viewBox="0 0 200 6"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M0,3 Q25,1 50,3 T100,3 T150,3 T200,3"
                            fill="none"
                            stroke="#cfeaf6"
                            strokeWidth="1.25"
                            strokeLinecap="round"
                            opacity="0.55"
                          />
                        </svg>
                      </span>
                    </span>
                  ))}
                </span>
                <span className="block mt-2">that wins you customers</span>
              </h1>

              <p
                className="text-base md:text-lg lg:text-xl text-white max-w-xl mx-auto lg:mx-0"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(16px)",
                  transition: "opacity 700ms ease-out, transform 700ms ease-out",
                  transitionDelay: "180ms",
                }}
              >
                We film it, edit it and put it in front of the right local people. Across North Wales and Shropshire.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4"
                style={{
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(16px)",
                  transition: "opacity 700ms ease-out, transform 700ms ease-out",
                  transitionDelay: "280ms",
                }}
              >
                <Button
                  size="lg"
                  className="magnetic-btn bg-white text-primary hover:bg-white/90 text-base px-7 py-5 border-2 border-white rounded-full"
                  asChild
                >
                  <a href={PHONE_TEL}>
                    Call us <ArrowRight className="ml-2" size={18} />
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="magnetic-btn border-2 border-white text-white bg-transparent hover:bg-white hover:text-primary text-base px-7 py-5 rounded-full transition-all duration-300"
                  asChild
                >
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">WhatsApp us</a>
                </Button>
              </div>
            </div>

            {/* Right side - Floating icon cluster */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative w-[340px] h-[340px]">
                {/* (Decorative rings removed — only icons animate now) */}


                {/* Centre - Meta */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div
                    className="w-24 h-24 rounded-2xl bg-white/20 border border-white/30 shadow-2xl shadow-black/10 flex items-center justify-center will-change-transform"
                    style={{ animation: "drift 9s ease-in-out 0.3s infinite" }}
                  >
                    <img src={metaLogo} alt="Meta" className="w-14 h-14 object-contain brightness-0 invert" />
                  </div>
                </div>

                {/* Orbiting icons - 5 max, clustered close to centre */}
                {[
                  { Icon: Instagram, top: "18%", left: "72%", size: 64, dur: "6s", delay: "0s", iconSize: 28 },
                  { Icon: Video, top: "76%", left: "28%", size: 60, dur: "6.5s", delay: "1.2s", iconSize: 26 },
                  { Icon: Facebook, top: "70%", left: "76%", size: 48, dur: "7s", delay: "0.9s", iconSize: 20 },
                  { Icon: TrendingUp, top: "22%", left: "24%", size: 52, dur: "5.6s", delay: "0.4s", iconSize: 22 },
                  { Icon: Youtube, top: "82%", left: "58%", size: 42, dur: "6.6s", delay: "1.6s", iconSize: 18 },
                ].map(({ Icon, top, left, size, dur, delay, iconSize }, i) => (
                  <div
                    key={i}
                    className="absolute"
                    style={{ top, left, width: size, height: size, transform: "translate(-50%, -50%)" }}
                  >
                    <div
                      className="w-full h-full rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center will-change-transform shadow-lg shadow-black/5"
                      style={{ animation: `drift calc(${dur} * 1.8) ease-in-out ${delay} infinite ${i % 2 ? "reverse" : "normal"}` }}
                    >
                      <Icon className="text-white" size={iconSize} strokeWidth={2} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeroV2;
